// src/components/TaskCard/styled.ts

import { motion } from "framer-motion";
import styled from "styled-components";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DialogContent } from "@/components/ui/dialog";
import { colourScheme } from "@/utils/styling/colours";

// Container for the TaskCard
export const TaskCardContainer = styled.div<{ completed: boolean }>`
    position: relative;
    padding: 1rem;
    border-radius: 0.5rem;
    border: 1px solid ${colourScheme.primaryLight};
    background: ${(props) =>
        props.completed
            ? `linear-gradient(135deg, ${colourScheme.darkGradientStart}, ${colourScheme.darkGradientEnd})`
            : `linear-gradient(135deg, ${colourScheme.primary}, ${colourScheme.primaryDark})`};
    box-shadow: ${(props) =>
        props.completed ? "none" : `0 2px 4px ${colourScheme.shadow}`};
    transition: all 0.3s ease;

    &:hover {
        box-shadow: 0 4px 8px ${colourScheme.shadow};
        border-color: ${colourScheme.secondary};
    }
`;

// Header section of the TaskCard
export const TaskCardHeader = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 0.5rem;
`;

// Icon container with dynamic background based on 'completed' prop
export const IconContainer = styled.div<{ completed: boolean }>`
    padding: 0.5rem;
    border-radius: 9999px;
    background-color: ${(props) =>
        props.completed ? colourScheme.primaryDark : colourScheme.primaryDark};
    margin-right: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
`;

// Task title with dynamic color based on 'completed' prop
export const TaskTitle = styled.h3<{ completed: boolean }>`
    font-size: 1rem;
    font-weight: 600;
    color: ${(props) =>
        props.completed ? colourScheme.greyLight : colourScheme.white};
`;

// Task description
export const TaskDescription = styled.p`
    font-size: 0.75rem;
    color: ${colourScheme.greyLight};
`;

// Reward container
export const RewardContainer = styled.div`
    display: flex;
    align-items: center;
`;

// Reward amount
export const GoldAmount = styled.span`
    color: ${colourScheme.goldLight};
    font-weight: bold;
    font-size: 0.875rem;
`;

// Progress bar container
export const ProgressBarContainer = styled.div`
    margin-bottom: 0.5rem;
`;

// Progress bar background
export const ProgressBarBackground = styled.div`
    background-color: ${colourScheme.primaryLight};
    height: 0.375rem;
    border-radius: 9999px;
`;

// Animated progress bar using framer-motion
export const ProgressBar = styled(motion.div)<{ progressColor: string }>`
    height: 0.375rem;
    border-radius: 9999px;
    background-color: ${(props) => props.progressColor};
`;

// Progress text
export const ProgressText = styled.p`
    text-align: right;
    font-size: 0.75rem;
    color: ${colourScheme.greyLight};
    margin-top: 0.125rem;
`;

// Footer section
export const TaskFooter = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
`;

// Styled Badge for tasks
export const StyledBadge = styled(Badge)<{
    badgeColor?: string;
    borderColor?: string;
}>`
    cursor: default;
    font-size: 0.75rem;
    border-color: ${(props) => props.borderColor || colourScheme.secondary};
    color: ${(props) => props.badgeColor || colourScheme.secondary};
    display: inline-flex; /* Use inline-flex to prevent expanding */
    align-items: center;
    padding: 0.25rem 0.5rem; /* Add some padding for better appearance */
    white-space: nowrap; /* Prevent text from wrapping */
`;

// Tooltip content styling
export const TooltipContentStyled = styled.div`
    background-color: ${colourScheme.primaryDark};
    color: ${colourScheme.white};
    border-color: ${colourScheme.secondary};
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem;
    border-radius: 0.375rem;
`;

// Status Badge for daily tasks
export const StatusBadge = styled(Badge)<{
    bgColor: string;
    textColor: string;
}>`
    font-size: 0.75rem;
    background-color: ${(props) => props.bgColor};
    color: ${(props) => props.textColor};
`;

// Styled Action Button
export const ActionButtonStyled = styled(Button)<{
    bgColor: string;
    hoverBgColor: string;
    textColor: string;
}>`
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    height: auto;
    background-color: ${(props) => props.bgColor};
    color: ${(props) => props.textColor};
    transition: background-color 0.3s ease;

    &:hover {
        background-color: ${(props) => props.hoverBgColor};
    }
`;

// Dialog Content styling
export const DialogContentStyled = styled(DialogContent)`
    background-color: ${colourScheme.primary};
    color: ${colourScheme.white};
    border-color: ${colourScheme.secondary};
    padding: 1rem;
    border-radius: 0.5rem;
`;

// Dialog Description styling
export const DialogDescriptionStyled = styled.p`
    color: ${colourScheme.greyLight};
    margin-top: 0.5rem;
`;

// Dialog Text container
export const DialogTextContainer = styled.div`
    color: ${colourScheme.white};
    margin-top: 1rem;
`;

// Badge Icon styling
export const BadgeIconStyled = styled.div<{ color?: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${(props) => props.color || colourScheme.secondary};
    width: 1rem;
    height: 1rem;
`;

export const ClaimButton = styled(Button)<{
    bgColor: string;
    textColor: string;
    hoverBgColor: string;
}>`
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    height: auto;
    background-color: ${({ bgColor }) => bgColor};
    color: ${({ textColor }) => textColor};
    transition: background-color 0.3s ease;

    &:hover {
        background-color: ${({ hoverBgColor }) => hoverBgColor};
    }

    &:disabled {
        background-color: ${colourScheme.greyDark};
        color: ${colourScheme.greyLight};
        cursor: not-allowed;
    }
`;
